import type { PulseGridConfig } from './types';

/** Default grid configuration — edge-to-edge, auto-sizing. */
export const DEFAULT_GRID_CONFIG: PulseGridConfig = {
  columns: 0, // 0 = auto-calculate from viewport
  gap: 0, // edge-to-edge by default
  rankIntervalSec: 8,
  maxAgeSec: 300, // 5 minutes before aging out
  transitionDurationMs: 700,
};

/** URL query param keys for overriding grid config. */
export const CONFIG_PARAMS = {
  columns: 'cols',
  gap: 'gap',
  rankInterval: 'rank',
  maxAge: 'maxAge',
  transition: 'transition',
} as const;

/** Minimum cell width in pixels — used for auto-column calculation.
 * Set narrow (portrait-friendly) since most TipMi content is 9:16 mobile video. */
export const MIN_CELL_WIDTH = 140;

/** Aspect ratio for cells (16:9). */
export const CELL_ASPECT_RATIO = 9 / 16;

/** How long (ms) the "NEW" badge pulses before fading. */
export const NEW_BADGE_DURATION_MS = 15_000;

/** How long (ms) a tip popup stays visible. */
export const TIP_POPUP_DURATION_MS = 3_000;

/** How long (ms) the deleted X overlay shows before the cell is removed. */
export const DELETED_LINGER_MS = 4_000;

/** How long (ms) moderated content fades to red. */
export const MODERATED_FADE_MS = 2_000;

/** Maximum number of cells to render (performance bound). */
export const MAX_GRID_CELLS = 60;

/** SSE reconnect delay (ms). */
export const SSE_RECONNECT_DELAY_MS = 3_000;

/** Anti-burn-in drift: pixels to shift per interval. */
export const BURNIN_DRIFT_PX = 1;
/** Anti-burn-in drift interval (ms). */
export const BURNIN_DRIFT_INTERVAL_MS = 30_000;

/**
 * Content mix targets (approximate percentages).
 * The ranking algorithm aims for this distribution.
 */
export const CONTENT_MIX = {
  /** High-velocity / trending content. */
  highVelocity: 0.4,
  /** Freshly uploaded (new). */
  newUploads: 0.3,
  /** Steady performers. */
  steady: 0.2,
  /** Transitioning out / aging. */
  aging: 0.1,
} as const;
